import { css, html, nothing } from "lit";
import { query, queryAssignedElements, queryAll, state } from "lit/decorators.js";
import { ifDefined } from "lit-html/directives/if-defined.js";
import "../Dropdown/index.js";
import "../DropdownContent/index.js";
import { DsfOption } from "../Option/index.js";
import { DsfOptionGroup } from "../OptionGroup/index.js";
import "../Checkbox/index.js";
import "../../atom/ClickOutside/index.js";
import { BaseComponent } from "../../BaseComponent.js";
import { prop } from "../../../decorators/prop/index.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { getLabelClasses, getMessageClasses } from "./utils.js";
import { DsfSelectButton } from "../SelectButton/index.js";
import { DsfFilterButton } from "../FilterButton/index.js";
import { DsfSearchInput } from "../SearchInput/index.js";
import { preventDefault } from "../../../decorators/preventDefault.js";
import { DsfPaginationSizeButton } from "../PaginationSizeButton/index.js";
import { stringOrStringArrayHasChanged } from "../change-detection.js";
import { testingAttr } from "../../../directives/testingAttr.js";
import { TestingAttrs } from "./selectBase.selectors.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
const sizes = ["medium", "small"];
const DSF_SELECT_ALL_VALUE = "dsf-select-all";
let DsfSelectBase = class extends BaseComponent {
  constructor() {
    super();
    this.value = [];
    this.disabled = false;
    this.dsf_open = false;
    this.dsf_error = false;
    this.dsf_success = false;
    this.dsf_size = "medium";
    this.dsf_flip = true;
    this.dsf_shift = true;
    this.dsf_hide = false;
    this.dsf_multiselect = false;
    this.dsf_show_close_button = false;
    this.dsf_searching = false;
    this.dsf_search_error = false;
    this.dsf_same_width = false;
    this.dsf_selected_first = false;
    this.dsf_open_on_focus = false;
    this.dsf_disable_search = false;
    this._selectValue = [];
    this.optionState = [];
    this._searchTerm = "";
    this._shouldOpenOnFocus = false;
    this.handleSlotChange = this.handleSlotChange.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.addEventListener("keydown", this.handleKeydown);
  }
  get optionElements() {
    return [...this._optionElements];
  }
  get selectAllOption() {
    return this.optionState.find((option) => option.value === DSF_SELECT_ALL_VALUE);
  }
  get buttonComponent() {
    if (!this._buttonAssignedElements.length) {
      throw new Error("dsf-select-base: #button slot is empty");
    }
    const buttonElement = this._buttonAssignedElements[0];
    if (!(buttonElement instanceof DsfSelectButton) && !(buttonElement instanceof DsfSearchInput) && !(buttonElement instanceof DsfFilterButton) && !(buttonElement instanceof DsfPaginationSizeButton)) {
      throw new Error(
        `dsf-select-base #button slot must contain a valid instance of either
          - dsf-select-button
          - dsf-search-input
          - dsf-filter-button
          - dsf-pagination-size-button
        `
      );
    }
    return buttonElement;
  }
  get defaultSlot() {
    return this.querySelector("slot");
  }
  get slotElements() {
    return this.defaultSlot.assignedElements().filter((el) => el instanceof DsfOption || el instanceof DsfOptionGroup);
  }
  get applyOnSelect() {
    return !this.dsf_apply_button_text;
  }
  connectedCallback() {
    super.connectedCallback();
    const rootNode = this.getRootNode();
    if (!(rootNode instanceof ShadowRoot)) {
      throw new Error(
        "dsf-select-base: component should not be used directly. Use dsf-select, dsf-filter or dsf-autocomplete instead."
      );
    }
    this.addEventListener("input", this.handleSearch);
    this.defaultSlot.addEventListener("slotchange", this.handleSlotChange);
  }
  disconnectedCallback() {
    var _a;
    super.disconnectedCallback();
    this.removeEventListener("input", this.handleSearch);
    this.defaultSlot.removeEventListener("slotchange", this.handleSlotChange);
    (_a = this._optionsMutationObserver) == null ? void 0 : _a.disconnect();
    this._optionsMutationObserver = void 0;
  }
  update(changedProps) {
    if (changedProps.has("value")) {
      this.syncSelectedOptionsFromValue();
    }
    if (this.dsf_multiselect && this.dsf_search_type !== "external" && changedProps.has("dsf_select_all_text")) {
      if (!this.dsf_select_all_text && this.selectAllOption) {
        this.optionState = this.optionState.filter((o) => o.value !== DSF_SELECT_ALL_VALUE);
      } else if (this.dsf_select_all_text && !this.selectAllOption) {
        this.addSelectAllOption();
      }
    }
    if (changedProps.has("dsf_open_on_focus")) {
      this._shouldOpenOnFocus = this.dsf_open_on_focus;
    }
    if (changedProps.has("_searchTerm") && changedProps.get("_searchTerm") !== void 0) {
      this.doSearch();
    }
    super.update(changedProps);
  }
  updated(changedProps) {
    if (changedProps.has("dsf_open") && this.dsf_open) {
      const selected = this.optionState.find((o) => o.selected);
      void this.scrollOptionIntoView(selected == null ? void 0 : selected.value);
      !this.dsf_disable_search && this.dropdownContent.focusInit();
    }
    if (changedProps.has("value")) {
      this.updateButtonSelectedOptions();
    }
    super.updated(changedProps);
  }
  syncSelectedOptionsFromValue() {
    this._selectValue = this.value;
    const selectedOptions = this.optionState.filter((o) => this._selectValue.includes(o.value));
    this.syncSelectedOptions(selectedOptions);
  }
  handleSlotChange() {
    if (!this._optionsMutationObserver) {
      this._optionsMutationObserver = new MutationObserver(() => {
        this.handleClientOptionsChange();
      });
    }
    const observer = this._optionsMutationObserver;
    this.slotElements.forEach((el) => {
      observer.observe(el, { attributes: true, childList: true });
      if (el instanceof DsfOptionGroup) {
        el.optionElements.forEach((o) => observer.observe(o, { attributes: true, childList: true }));
      }
    });
    this.handleClientOptionsChange();
  }
  mapOptionState(option, group) {
    const value = String(option.value);
    return {
      value,
      label: option.getOptionText(),
      group,
      icon: option.querySelector(`[slot="${DsfOption.iconSlot}"]`),
      disabled: option.disabled,
      selected: this._selectValue.includes(value),
      multiselect: this.dsf_multiselect,
      active: false,
      hidden: this.getOptionHiddenState(option)
    };
  }
  handleClientOptionsChange() {
    const newOptionState = [];
    this.slotElements.forEach((el) => {
      if (el instanceof DsfOption) {
        newOptionState.push(this.mapOptionState(el));
      } else {
        newOptionState.push(...el.optionElements.map((o) => this.mapOptionState(o, el.dsf_title)));
      }
    });
    let previouslySelectedOptions = [];
    if (this.dsf_search_type === "external") {
      previouslySelectedOptions = this.optionState.filter(
        (option) => this._selectValue.includes(option.value) && !newOptionState.find((newOption) => newOption.value === option.value)
      );
      previouslySelectedOptions.forEach((option) => option.hidden = true);
    }
    this.optionState = [...newOptionState, ...previouslySelectedOptions];
    if (this.dsf_multiselect && this.dsf_search_type !== "external" && this.dsf_select_all_text) {
      this.addSelectAllOption();
    }
  }
  updateButtonSelectedOptions() {
    const selectedOptions = this.optionState.filter((o) => this._selectValue.includes(o.value));
    if (this.buttonComponent instanceof DsfSearchInput)
      ;
    else if (this.buttonComponent instanceof DsfSelectButton) {
      this.buttonComponent.dsf_options = selectedOptions.map((o) => ({
        label: o.label,
        icon: o.icon
      }));
    } else {
      this.buttonComponent.dsf_options = selectedOptions.map((o) => o.label);
    }
  }
  addSelectAllOption() {
    this.optionState.unshift({
      value: DSF_SELECT_ALL_VALUE,
      label: "",
      icon: null,
      disabled: false,
      selected: this.optionState.filter((o) => !o.disabled).every((o) => o.selected),
      multiselect: true,
      active: false,
      hidden: !!this._searchTerm
    });
  }
  getOptionHiddenState(option) {
    if (this.dsf_search_type === "local") {
      return option.hidden || !this.optionMatchesSearch(option.getOptionText());
    }
    return option.hidden;
  }
  mutateOptionState(mutator) {
    mutator();
    this.requestUpdate("optionState");
  }
  sortSelectedOptions(options) {
    const sortFunc = (o1, o2) => {
      if (o2.value === DSF_SELECT_ALL_VALUE) {
        return 1;
      }
      return o1.selected === o2.selected ? 0 : o1.selected ? -1 : 1;
    };
    return options.sort(sortFunc);
  }
  getOptionsToNavigate() {
    const filteredOptions = this.optionState.filter((o) => !o.disabled && !o.hidden);
    return this.dsf_selected_first ? this.sortSelectedOptions(filteredOptions) : filteredOptions;
  }
  handleKeydown(ev) {
    if (this.disabled) {
      return;
    }
    const key = ev.key;
    if (!this.dsf_open && ["ArrowUp", "ArrowDown", "Enter", " "].includes(key)) {
      this.openDropdown();
      ev.preventDefault();
    } else if (this.dsf_open) {
      const isContentFocused = this.dropdownContent.isContentFocused;
      const activeOption = this.optionState.find((o) => o.active);
      const optionsToNavigate = this.getOptionsToNavigate();
      const otherOptions = this.optionState.filter((o) => !optionsToNavigate.includes(o));
      const syncActiveOption = (index) => this.mutateOptionState(() => {
        optionsToNavigate.forEach((o, i) => o.active = i === index);
        otherOptions.forEach((o) => o.active = false);
      });
      if (isContentFocused && ["ArrowUp", "ArrowDown"].includes(key)) {
        const indexIncrement = key === "ArrowDown" ? 1 : -1;
        let nextIndex = activeOption ? optionsToNavigate.indexOf(activeOption) + indexIncrement : 0;
        if (nextIndex < 0) {
          nextIndex = optionsToNavigate.length - 1;
        }
        if (nextIndex > optionsToNavigate.length - 1) {
          nextIndex = 0;
        }
        syncActiveOption(nextIndex);
        void this.scrollOptionIntoView(optionsToNavigate[nextIndex].value);
        ev.preventDefault();
      } else if (isContentFocused && ["Enter", " "].includes(key)) {
        if (activeOption) {
          this.selectOption(activeOption);
          void this.scrollOptionIntoView(activeOption.value);
        }
        ev.preventDefault();
      } else if (["Tab"].includes(key)) {
        this.dropdownContent.moveFocusToNextElement();
        if (isContentFocused) {
          if (!optionsToNavigate.length) {
            this.dropdownContent.moveFocusToNextElement();
          } else if (!activeOption || !optionsToNavigate.includes(activeOption)) {
            syncActiveOption(0);
          }
        }
        ev.preventDefault();
      } else if (["Escape"].includes(key)) {
        this.closeAndFocus();
      }
    }
  }
  openDropdown() {
    const firstSelected = this.optionState.find((o) => o.selected);
    if (firstSelected) {
      firstSelected.active = true;
    }
    this.dsf_open = true;
    this.emitOpenChanged();
  }
  focus() {
    this.buttonComponent.focus();
  }
  closeAndFocus() {
    this.closeDropdown();
    this._shouldOpenOnFocus = false;
    !this.dsf_disable_search && this.focus();
  }
  handleAnchorFocusIn() {
    if (this._shouldOpenOnFocus) {
      this.openDropdown();
    }
    this._shouldOpenOnFocus = this.dsf_open_on_focus;
  }
  syncSelectedOptions(options) {
    this._selectValue = options.map((o) => o.value);
    this.mutateOptionState(() => {
      this.optionState.forEach((o) => o.selected = this._selectValue.includes(o.value));
      if (this.selectAllOption) {
        const isAllSelected = this.optionState.slice(1).filter((o) => !o.disabled).every((o) => o.selected);
        this.optionState[0].selected = isAllSelected;
      }
    });
  }
  selectOption(option) {
    if (option.disabled) {
      return;
    }
    if (!this.dsf_multiselect) {
      this.syncSelectedOptions([option]);
      if (this.applyOnSelect) {
        this.setValue();
        !this.dsf_disable_search ? this.closeAndFocus() : this.closeDropdown();
      }
      this.emitSelectOption([option]);
    } else {
      const options = this.selectAllOption ? this.optionState.slice(1) : this.optionState;
      if (option.value === DSF_SELECT_ALL_VALUE) {
        const optionsToSelect = options.filter((o) => !o.disabled);
        const alwaysSelected = options.filter((o) => o.disabled && o.selected);
        const isAllSelected = optionsToSelect.every((o) => o.selected);
        if (!isAllSelected) {
          this.syncSelectedOptions([...optionsToSelect, ...alwaysSelected]);
          this.emitSelectOption(optionsToSelect);
        } else {
          this.syncSelectedOptions([...alwaysSelected]);
          this.emitSelectOption(optionsToSelect.filter((o) => !alwaysSelected.includes(o)));
        }
      } else {
        const newValue = this._selectValue.includes(option.value) ? this._selectValue.filter((v) => v !== option.value) : [...this._selectValue, option.value];
        this.syncSelectedOptions(options.filter((o) => newValue.includes(o.value)));
        this.emitSelectOption([option]);
      }
      if (this.applyOnSelect) {
        this.setValue();
      }
    }
  }
  handleSelectMousedown() {
    if (this.disabled) {
      return;
    }
    if (this.dsf_disable_search) {
      this.dsf_open = true;
    } else {
      this.dsf_open = !this.dsf_open;
    }
    this.emitOpenChanged();
  }
  handleOptionsClick(ev) {
    if (this.disabled || !(ev.target instanceof DsfOption)) {
      return;
    }
    const option = ev.target;
    const optionState = this.optionState.find((o) => o.value === option.value);
    this.selectOption(optionState);
  }
  closeDropdown() {
    if (!this.dsf_open) {
      return;
    }
    this.dsf_open = false;
    this.emitOpenChanged();
    this.syncSelectedOptionsFromValue();
    this.dropdownContent.clearSearch();
    this._searchTerm = "";
    this.mutateOptionState(() => {
      this.optionState.forEach((o) => {
        o.active = false;
      });
      if (this.selectAllOption) {
        this.selectAllOption.hidden = false;
      }
    });
  }
  async scrollOptionIntoView(optionValue) {
    await this.updateComplete;
    const option = this.optionElements.find((o) => o.value === optionValue);
    if (option) {
      setTimeout(
        () => option.scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: "smooth"
        }),
        0
      );
    } else {
      this.dropdownContent.resetScroll();
    }
  }
  emitOpenChanged() {
    this.emit({ name: "open-changed", payload: this.dsf_open });
  }
  emitSelectOption(options) {
    options.forEach(
      (o) => this.emit({ name: o.selected ? "select-option" : "unselect-option", payload: o.value })
    );
  }
  handleCloseClick() {
    this.closeAndFocus();
  }
  handleClearClick() {
    this.syncSelectedOptions([]);
    this.setValue();
    this.emit("clear");
  }
  handleApplyClick() {
    this.setValue();
    this.emit("apply");
    this.closeAndFocus();
  }
  setValue() {
    const oldValue = this.value;
    this.value = this._selectValue;
    if (stringOrStringArrayHasChanged(oldValue, this.value)) {
      this.emit({ name: "change", payload: this.value });
    }
  }
  handleSearch(ev) {
    if (this.dsf_search_type !== "local") {
      return;
    }
    this._searchTerm = ev.detail.toLowerCase();
  }
  doSearch() {
    const matchingOptions = this.optionState.filter(
      (o) => o.value === DSF_SELECT_ALL_VALUE && !this._searchTerm || o.value !== DSF_SELECT_ALL_VALUE && this.optionMatchesSearch(o.label)
    );
    this.mutateOptionState(
      () => this.optionState.forEach((o) => {
        var _a;
        const optionElementHidden = (_a = this.slotElements.find(
          (el) => el instanceof DsfOption && el.value === o.value
        )) == null ? void 0 : _a.hidden;
        o.hidden = optionElementHidden || !matchingOptions.includes(o);
      })
    );
  }
  optionMatchesSearch(optionLabel) {
    return !this._searchTerm || optionLabel.toLowerCase().includes(this._searchTerm);
  }
  renderOption(option) {
    var _a;
    return html`<dsf-option
      value=${ifDefined(option.value)}
      .hidden=${option.hidden}
      ?disabled=${option.disabled}
      ?dsf_multiselect=${this.dsf_multiselect}
      ?dsf_selected=${option.selected}
      ?dsf_active=${option.active}
      style=${ifDefined(option.hidden ? "display: none" : void 0)}
      @click=${this.handleOptionsClick}
    >
      ${(_a = option.icon) == null ? void 0 : _a.cloneNode()}
      ${option.value === DSF_SELECT_ALL_VALUE ? this.dsf_select_all_text : option.label}
    </dsf-option>`;
  }
  renderMultiSelectOptions() {
    if (this.dsf_selected_first) {
      const selectAll = this.selectAllOption;
      const options = selectAll ? this.optionState.slice(1) : this.optionState;
      const selected = options.filter((o) => o.selected);
      const notSelected = options.filter((o) => !o.selected);
      const showDivider = selected.filter((o) => !o.hidden).length && notSelected.filter((o) => !o.hidden).length;
      return html`
        ${selectAll ? this.renderOption(selectAll) : nothing}
        ${selected.map((o) => this.renderOption(o))}
        ${showDivider ? html`<hr class="text-neutral-100" />` : nothing}
        ${notSelected.map((o) => this.renderOption(o))}
      `;
    }
    return this.renderOptionGroups();
  }
  renderSingleSelectOptions() {
    if (this.dsf_selected_first) {
      const selected = this.optionState.filter((o) => o.selected);
      const notSelected = this.optionState.filter((o) => !o.selected);
      return html`
        ${selected.map((o) => this.renderOption(o))} ${notSelected.map((o) => this.renderOption(o))}
      `;
    }
    return this.renderOptionGroups();
  }
  renderOptionGroups() {
    const groups = [];
    this.optionState.forEach((option) => {
      const group = option.group ? groups.find((g) => g.title === option.group) : void 0;
      if (group) {
        group.options.push(option);
      } else {
        groups.push({ title: option.group, options: [option] });
      }
    });
    return groups.map((group) => {
      const isGroupHidden = group.options.every((o) => o.hidden);
      return group.title ? html`<dsf-option-group
            dsf_title=${ifDefined(group.title)}
            .hidden=${isGroupHidden}
            class="mt-8"
            style=${ifDefined(isGroupHidden ? "display: none" : void 0)}
          >
            ${group.options.map((o) => this.renderOption(o))}
          </dsf-option-group>` : html`${group.options.map((o) => this.renderOption(o))}`;
    });
  }
  render() {
    const label = html`<label id="label" class=${getLabelClasses(this)} data-skeleton>
      ${this.dsf_label || this.dsf_accessibility_label}
    </label>`;
    const message = this.dsf_message ? html`<span class="${getMessageClasses(this)}">${this.dsf_message}</span>` : nothing;
    const options = this.dsf_multiselect ? this.renderMultiSelectOptions() : this.renderSingleSelectOptions();
    const showNoResultsLabel = !this.optionState.length || !this.optionState.some((o) => !o.hidden);
    return html`
      <dsf-click-outside
        class="flex min-w-0 flex-auto flex-col font-sans text-neutral-700"
        @click-outside=${this.closeDropdown}
      >
        ${label}
        <dsf-dropdown
          class="flex flex-auto flex-col"
          ?dsf_flip=${this.dsf_flip}
          ?dsf_shift=${this.dsf_shift}
          ?dsf_hide=${this.dsf_hide}
          .dsf_open=${this.dsf_open}
          .dsf_same_width=${this.dsf_same_width}
          .dsf_offset=${4}
          data-skeleton
        >
          <div
            slot="anchor"
            role="combobox"
            aria-labelledby="label"
            aria-disabled=${this.disabled ? "true" : "false"}
            aria-expanded=${this.dsf_open ? "true" : "false"}
            aria-controls="list"
            @mousedown=${this.handleSelectMousedown}
            @focusin=${this.handleAnchorFocusIn}
          >
            <slot name="button">
              <div class="dsf-text-s">Default button content</div>
            </slot>
          </div>

          <div
            id="list"
            class="flex flex-auto flex-col overflow-visible rounded-sm border border-neutral-100 bg-neutral-0 shadow-xl"
            part="dropdown"
            role="listbox"
            aria-labelledby="label"
            aria-expanded=${this.dsf_open ? "true" : "false"}
          >
            <dsf-dropdown-content
              id="dropdownContent"
              class="min-h-0"
              exportparts="content: list"
              .dsf_title_text=${this.dsf_title_text}
              .dsf_show_close_button=${this.dsf_show_close_button}
              .dsf_clear_button_text=${this.dsf_clear_button_text}
              .dsf_apply_button_text=${this.dsf_apply_button_text}
              .dsf_show_search=${!!this.dsf_search_type && !this.dsf_disable_search}
              .dsf_search_placeholder=${this.dsf_search_placeholder}
              .dsf_searching=${this.dsf_searching}
              .dsf_search_message=${this.dsf_search_message}
              .dsf_search_error=${this.dsf_search_error}
              @close-click=${this.handleCloseClick}
              @clear-click=${this.handleClearClick}
              @apply-click=${this.handleApplyClick}
              @search=${this.handleSearch}
              ${testingAttr(TestingAttrs.dropdownContent)}
            >
              ${options}
              ${showNoResultsLabel && this.dsf_no_results_text ? html`<span class="dsf-text-xs m-12 flex font-sans text-neutral-400">
                    ${this.dsf_no_results_text}
                  </span>` : nothing}
            </dsf-dropdown-content>
          </div>
        </dsf-dropdown>
        ${message}
      </dsf-click-outside>
    `;
  }
};
DsfSelectBase.styles = [
  ...BaseComponent.styles,
  css`
      :host {
        display: inline-flex;
        flex: 1 1 auto;
        min-width: 0;
      }

      dsf-dropdown::part(dropdown) {
        z-index: var(--dsf-z-index-dropdown);
      }
    `
];
__decorateClass([
  prop({
    type: String,
    converter: {
      fromAttribute: (value) => (value == null ? void 0 : value.split(" ")) || []
    },
    hasChanged: stringOrStringArrayHasChanged
  })
], DsfSelectBase.prototype, "value", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectBase.prototype, "disabled", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectBase.prototype, "dsf_open", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectBase.prototype, "dsf_error", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectBase.prototype, "dsf_success", 2);
__decorateClass([
  prop({ type: String })
], DsfSelectBase.prototype, "dsf_message", 2);
__decorateClass([
  prop({ type: String })
], DsfSelectBase.prototype, "dsf_label", 2);
__decorateClass([
  prop({ type: String, reflect: true, validator: (value) => sizes.includes(value) })
], DsfSelectBase.prototype, "dsf_size", 2);
__decorateClass([
  prop({ type: String })
], DsfSelectBase.prototype, "dsf_accessibility_label", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectBase.prototype, "dsf_flip", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectBase.prototype, "dsf_shift", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectBase.prototype, "dsf_hide", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectBase.prototype, "dsf_multiselect", 2);
__decorateClass([
  prop({ type: String })
], DsfSelectBase.prototype, "dsf_title_text", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectBase.prototype, "dsf_show_close_button", 2);
__decorateClass([
  prop({ type: String })
], DsfSelectBase.prototype, "dsf_clear_button_text", 2);
__decorateClass([
  prop({ type: String })
], DsfSelectBase.prototype, "dsf_apply_button_text", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfSelectBase.prototype, "dsf_search_type", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectBase.prototype, "dsf_searching", 2);
__decorateClass([
  prop({ type: String })
], DsfSelectBase.prototype, "dsf_search_placeholder", 2);
__decorateClass([
  prop({ type: String })
], DsfSelectBase.prototype, "dsf_search_message", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectBase.prototype, "dsf_search_error", 2);
__decorateClass([
  prop({ type: String })
], DsfSelectBase.prototype, "dsf_no_results_text", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectBase.prototype, "dsf_same_width", 2);
__decorateClass([
  prop({ type: String })
], DsfSelectBase.prototype, "dsf_select_all_text", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectBase.prototype, "dsf_selected_first", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectBase.prototype, "dsf_open_on_focus", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectBase.prototype, "dsf_disable_search", 2);
__decorateClass([
  query("dsf-dropdown-content")
], DsfSelectBase.prototype, "dropdownContent", 2);
__decorateClass([
  queryAssignedElements({ slot: "button", selector: "*" })
], DsfSelectBase.prototype, "_buttonAssignedElements", 2);
__decorateClass([
  queryAll("dsf-option")
], DsfSelectBase.prototype, "_optionElements", 2);
__decorateClass([
  state()
], DsfSelectBase.prototype, "_selectValue", 2);
__decorateClass([
  state()
], DsfSelectBase.prototype, "optionState", 2);
__decorateClass([
  state()
], DsfSelectBase.prototype, "_searchTerm", 2);
__decorateClass([
  preventDefault
], DsfSelectBase.prototype, "handleCloseClick", 1);
__decorateClass([
  preventDefault
], DsfSelectBase.prototype, "handleClearClick", 1);
__decorateClass([
  preventDefault
], DsfSelectBase.prototype, "handleApplyClick", 1);
DsfSelectBase = __decorateClass([
  defineElement("dsf-select-base")
], DsfSelectBase);
export {
  DsfSelectBase,
  sizes
};
